import { useState, useEffect, useRef, useCallback } from 'react';
import { useFeatureFlag } from '../../../stores/featureFlags';
import { isLiveSessionDeliveryEnabled, isPeerLedGroupCoaching } from '../../../utils/liveSessionDelivery';
import { Stack, Center, Loader, Alert } from '@mantine/core';
import { AlertCircle } from 'react-feather';
import type { CohortWithDetails, SessionDrillDownCard } from '../../../types';
import { useHealthSummary } from '../hooks/useHealthSummary';
import { useModalityProgress } from '../hooks/useModalityProgress';
import { CohortHealthSummary } from './health';
import { HealthStatCardsRow, type StatCardKey } from './health/HealthStatCardsRow';
import { SessionHealthDrillDown } from './health/SessionHealthDrillDown';
import { ParticipantHealthDrillDown } from './health/ParticipantHealthDrillDown';
import { AttendanceHealthDrillDown } from './health/AttendanceHealthDrillDown';
import { SatisfactionDrillDown } from './health/SatisfactionDrillDown';
import { ModalityProgressSection } from './overview/ModalityProgressSection';
import { TaskModal } from './TaskModal';

// E19-6: Callback to open the follow-ups pane with optional targeting
export type OpenFollowUpsFn = (options?: { sessionId?: string; participantId?: string }) => void;

interface OverviewTabProps {
  cohort: CohortWithDetails;
  onOpenSessionConfigModal: () => void;
  onOpenTeamModal: () => void;
  onOpenConstraintsModal: () => void;
  onOpenPrimaryContactsModal: () => void;
  onOpenJobAssignmentsModal: () => void;
  // MED-204: Refresh key to trigger data reload when tasks change
  refreshKey?: number;
  isActive?: boolean;
  onHealthChange?: () => void;
  onOpenFollowUps?: OpenFollowUpsFn;
}

export function OverviewTab({
  cohort,
  refreshKey,
  isActive,
  onHealthChange,
  onOpenFollowUps,
}: OverviewTabProps) {
  const multiModalityEnabled = useFeatureFlag('multi_modality');
  // MED-1205 (AC6): the leader-airtime metric on Session Health cards is gated
  // to group-coaching cohorts with live_session_delivery on — the same two-part
  // gate used elsewhere (global flag AND per-cohort modality toggle). isPeerLed
  // is a SEPARATE axis: it only picks the airtime color thresholds (AC2).
  const liveSessionDeliveryEnabled = useFeatureFlag('live_session_delivery');
  const showAirtimeMetric = liveSessionDeliveryEnabled && isLiveSessionDeliveryEnabled(cohort);
  const isPeerLedCohort = isPeerLedGroupCoaching(cohort);
  const { healthSummary, loading: healthLoading, error: healthError, refresh: refreshHealth } = useHealthSummary({
    cohortId: cohort.id,
    enabled: isActive,
  });

  const { cards: modalityCards } = useModalityProgress({
    cohortId: cohort.id,
    enabled: isActive,
  });

  // MED-419/420: Stat card selection and drill-down state
  const [selectedStatCard, setSelectedStatCard] = useState<StatCardKey | null>(null);
  const drillDownRef = useRef<HTMLDivElement>(null);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [taskDefaults, setTaskDefaults] = useState<{ title: string; description: string } | null>(null);

  // MED-204: Refresh health data when refreshKey changes (after task status update)
  useEffect(() => {
    if (refreshKey && refreshKey > 0) {
      refreshHealth();
    }
  }, [refreshKey, refreshHealth]);

  // MED-473: Scroll drill-down into view (reused for stat card selection + tab changes)
  const scrollDrillDownIntoView = useCallback(() => {
    setTimeout(() => {
      drillDownRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 150);
  }, []);

  // MED-473: Scroll drill-down into view when a stat card is selected
  useEffect(() => {
    if (!selectedStatCard) return;
    scrollDrillDownIntoView();
  }, [selectedStatCard, scrollDrillDownIntoView]);

  const handleDrillDownAddTask = (session: SessionDrillDownCard) => {
    setTaskDefaults({
      title: `${session.title} needs attention`,
      description: `Session ${session.sessionNumber} (${session.title}) has been flagged for review.\n\nMetrics:\n- Attendance: ${session.metrics.attendPercent ?? 'N/A'}%\n- Engagement: ${session.metrics.engageAvg ?? 'N/A'}\n- RSVP: ${session.metrics.rsvpPercent ?? 'N/A'}%`,
    });
    setTaskModalOpen(true);
  };

  // MED-424: Handle Add Task from participant health drill-down
  const handleParticipantAddTask = (defaults: { title: string; description: string }) => {
    setTaskDefaults(defaults);
    setTaskModalOpen(true);
  };

  if (healthLoading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  if (healthError || !healthSummary) {
    return (
      <Alert
        icon={<AlertCircle size={16} />}
        color="red"
        title="Error loading health summary"
      >
        {healthError || 'Failed to load health summary'}
      </Alert>
    );
  }

  return (
    <Stack gap="xl">
      {/* MED-1038 (E1.10d): the standalone Facilitation Configuration callout was removed
          here — its sub-type / Live Delivery / AI content now lives on the modality
          headers themselves as config pills (Modality Progress cards + Sessions tab). */}

      {/* MED-570 / MED-578: Modality Progress — gated by multi_modality flag */}
      {/* MED-1038: pass cohort.modalities so each card header shows its config pills */}
      {multiModalityEnabled && (
        <ModalityProgressSection cards={modalityCards} modalities={cohort.modalities} />
      )}

      {/* Health Summary — alerts container */}
      <CohortHealthSummary
        cohortId={cohort.id}
        healthSummary={healthSummary}
        onRefresh={refreshHealth}
        onHealthChange={onHealthChange}
        onOpenFollowUps={onOpenFollowUps}
      />

      {/* MED-419: Stat cards — below health summary */}
      <HealthStatCardsRow
        statCards={healthSummary.statCards}
        selectedCard={selectedStatCard}
        onSelectCard={setSelectedStatCard}
      />

      {/* MED-473: Drill-down container with scroll ref */}
      {selectedStatCard && (
        <div ref={drillDownRef}>
          {/* MED-420: Session drill-down */}
          {selectedStatCard === 'sessions' && healthSummary.sessionDrillDown && (
            <SessionHealthDrillDown
              drillDown={healthSummary.sessionDrillDown}
              cohortId={cohort.id}
              onAddTask={handleDrillDownAddTask}
              onTabChange={scrollDrillDownIntoView}
              showAirtime={showAirtimeMetric}
              isPeerLed={isPeerLedCohort}
            />
          )}

          {/* MED-424: Participant health drill-down */}
          {selectedStatCard === 'participants' && (
            <ParticipantHealthDrillDown
              cohortId={cohort.id}
              flaggedCount={parseInt(healthSummary.statCards.participants?.badgeLabel?.replace(/\D/g, '') || '0', 10)}
              onAddTask={handleParticipantAddTask}
              onTabChange={scrollDrillDownIntoView}
              onOpenFollowUps={onOpenFollowUps}
            />
          )}

          {/* MED-425: Attendance drill-down */}
          {selectedStatCard === 'attendance' && (
            <AttendanceHealthDrillDown
              cohortId={cohort.id}
              onAddTask={handleParticipantAddTask}
              onTabChange={scrollDrillDownIntoView}
              onOpenFollowUps={onOpenFollowUps}
            />
          )}

          {/* MED-426: Satisfaction drill-down */}
          {selectedStatCard === 'satisfaction' && (
            <SatisfactionDrillDown
              cohortId={cohort.id}
              onAddTask={handleParticipantAddTask}
              onTabChange={scrollDrillDownIntoView}
            />
          )}
        </div>
      )}

      {/* MED-420: Task modal for drill-down "Add Task" */}
      <TaskModal
        opened={taskModalOpen}
        onClose={() => {
          setTaskModalOpen(false);
          setTaskDefaults(null);
        }}
        cohortId={cohort.id}
        defaultTitle={taskDefaults?.title}
        defaultDescription={taskDefaults?.description}
        onSaved={() => {
          setTaskModalOpen(false);
          setTaskDefaults(null);
        }}
      />
    </Stack>
  );
}

export default OverviewTab;
